import React from "react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

import type * as type from "@vTypes";
import WeeklyOffTable from "./WeeklyOffTable";
import { useWeeklyOffSettingsQuery } from "@hooks/useWeeklyOffSettingsQuery";
import { useFullNurseListQuery } from "@hooks/useFullNurseList";
import { useSaveWeeklyOffSettingsMutation } from "@hooks/useSaveWeeklyOffSettingsMutation";
import { useSaveWeeklyOffNursesMutation } from "@hooks/useSaveWeeklyOffNursesMutation";
import ModalWeekOffFullNurse from "./ModalWeekOffFullNurse";
import { toast } from "@/components/toast/CustomToast";
import useQueryInvaildate from "@hooks/useQueryInvaildate";
import { QUERY_BASE_KEY } from "@constants/query-key";
import WeekOffAlertModal from "./WeekOffAlertModal";
import CustomModal from "@/components/modal/CustomModal";
import LoadingSpiner from "@components/ui/LoadingSpiner";
import WeeklyOffSettings from "./WeeklyOffSettings";
import { useWeeklyOffSettings } from "@hooks/weekly-off/useWeeklyOffSettings";
import WeeklyOffDayTabs from "./WeeklyOffDayTabs";
import { useWeeklyOffManagement } from "@hooks/weekly-off/useWeeklyOffManagement";
import WeeklyOffSearchBar from "./WeeklyOffSearchBar";
import { useWeeklyOffSearch } from "@hooks/weekly-off/useWeeklyOffSearch";
import WeeklyOffBottomButtons from "./WeeklyOffBottomButtons";
import { useUnsavedChanges } from "@hooks/weekly-off/useUnsavedChanges";
import { linkPreceptorRelations } from "@/utils/nurse-util";
import type { NurseResponse } from "@/types/nurse-type";

type DayOfWeek =
  | "총 배치인원"
  | "월요일"
  | "화요일"
  | "수요일"
  | "목요일"
  | "금요일"
  | "토요일"
  | "일요일";

type ActualDayOfWeek = Exclude<DayOfWeek, "총 배치인원">;

const ALL_ACTUAL_DAYS: ActualDayOfWeek[] = [
  "월요일",
  "화요일",
  "수요일",
  "목요일",
  "금요일",
  "토요일",
  "일요일",
];

interface DayTab {
  label: DayOfWeek;
  count: number;
}

interface ConfTab3Props {
  isAdmin: boolean;
  selectedGroupId: string | null;
  serverConfig: type.ConfigObject | undefined;
  visible?: boolean;
}

export interface ConfTab3Ref {
  hasUnsavedChanges: boolean;
  handleSave: (options?: { onSuccess?: () => void }) => void;
  handleReset: () => void;
}

const ConfTab3 = forwardRef<ConfTab3Ref, ConfTab3Props>(
  ({ isAdmin, selectedGroupId, serverConfig, visible }, ref) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // 전체 간호사 주휴배치 모달
    const [openModalWeekOffFullNurse, setOpenModalWeekOffFullNurse] =
      useState<boolean>(false);
    const [selectedDay, setSelectedDay] = useState<DayOfWeek>("월요일");
    // 변경사항 감지를 위한 상태
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // 저장 확인 모달 (저장하기 클릭 시 "저장할까요?")
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

    const { invalidate } = useQueryInvaildate();

    // 인스턴스 초기화
    const fetchNurseSchema = async (groupId?: string) => {
      const targetGroup = groupId || selectedGroupId;
      invalidate([QUERY_BASE_KEY.TEAMS, targetGroup as string]);
    };

    // 간호사 목록 조회
    const { data: allNurses, isLoading: isNursesListLoading } =
      useFullNurseListQuery({
        isAdmin: isAdmin,
        groupId: selectedGroupId ?? undefined,
      });
    /** 근무자 관리(Head_nurse_management)와 동일: 프리셉터 관계 연결 + sequence 정렬 */
    const nurses = useMemo(() => {
      const active = allNurses?.filter((n) => n.active !== 0);
      if (!active?.length) return [];
      const linked = linkPreceptorRelations(active as NurseResponse[]);
      return [...linked].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
      ) as type.FullNurseSchema[];
    }, [allNurses]);

    // 주휴 설정 조회
    const {
      data: weeklyOffSettingsData,
      refetch: refetchSettings,
      isLoading: isWeeklyOffSettingsLoading,
    } = useWeeklyOffSettingsQuery({
      isAdmin: isAdmin,
      groupId: selectedGroupId ?? undefined,
    });

    // 일단 여기서 간호사 목록 가져오는 api 호출
    useEffect(() => {
      fetchNurseSchema(isAdmin ? (selectedGroupId ?? undefined) : undefined);
    }, []);

    // ===== 커스텀 훅 사용 =====
    // 간호사 주휴 배치 관리
    const {
      assignedNursesByDay,
      unassignedNurses,
      nursesWithCurrentWeeklyOff,
      setAssignedNursesByDay,
      setUnassignedNurses,
      getAssignedDayForNurse,
      getPreceptorInfo,
      calculateChanges,
      hasNurseChanges,
      updateInitialAssignments,
      resetAssignments,
    } = useWeeklyOffManagement(nurses);

    // 설정 관리
    const {
      weeklyOffSettings,
      weekOffset,
      setWeekOffset,
      handleSettingsChange,
      formatDate,
      getDateValue,
      isValidDateString,
      hasSettingsChanges,
      updateInitialSettings,
      getSettingsPayload,
      resetSettings,
    } = useWeeklyOffSettings(weeklyOffSettingsData, refetchSettings);

    // 검색 기능
    const {
      searchTerm,
      highlightedNurseId,
      handleSearch,
      handleSearchKeyDown,
      handleSearchTermChange,
      resetSearch,
    } = useWeeklyOffSearch(nurses);

    // 요일 탭 변경 시 검색 초기화
    useEffect(() => {
      resetSearch();
    }, [selectedDay, resetSearch]);

    // ===== Mutations =====
    const { mutate: saveSettings, isPending: isSettingsSaving } =
      useSaveWeeklyOffSettingsMutation({
        isAdmin: isAdmin,
        groupId: selectedGroupId ?? undefined,
      });

    const { mutate: saveWeeklyOffNurses, isPending: isNursesSaving } =
      useSaveWeeklyOffNursesMutation(
        isAdmin ? (selectedGroupId ?? undefined) : undefined
      );

    // 저장 로직 pending 상태
    const isSaving = isSettingsSaving || isNursesSaving;

    // ===== 요일별 탭 데이터 =====
    const daysOfWeekTabs = useMemo(() => {
      const tabs: DayTab[] = ALL_ACTUAL_DAYS.map((day) => {
        const count = assignedNursesByDay[day]?.length || 0;
        return { label: day, count: count };
      });
      tabs.unshift({ label: "총 배치인원", count: nurses?.length || 0 });
      return tabs;
    }, [assignedNursesByDay, nurses]);

    // ===== 선택된 요일의 주휴배치 목록 업데이트 =====
    const handleSetAssignedNurses = useCallback(
      (value: React.SetStateAction<type.FullNurseSchema[]>) => {
        setAssignedNursesByDay((prev) => {
          if (selectedDay === "총 배치인원") {
            console.warn("Cannot directly modify '총 배치인원' list.");
            return prev;
          }
          const newNursesForDay =
            typeof value === "function" ? value(prev[selectedDay]) : value;
          return {
            ...prev,
            [selectedDay]: newNursesForDay,
          };
        });
      },
      [selectedDay, setAssignedNursesByDay]
    );

    // ===== 저장 로직 =====
    const handleSave = useCallback(
      (options?: { onSuccess?: () => void }) => {
        const nurseChanges = calculateChanges();
        const shouldSaveNurses = nurseChanges.length > 0;

        const settingsPayload = getSettingsPayload();

        if (!settingsPayload) {
          alert(
            "올바른 날짜 형식이 아닙니다. YYYY-MM-DD 형식으로 입력해주세요."
          );
          return;
        }

        // cycle_start_date보고 base_year와 base_month 설정
        if (settingsPayload && weeklyOffSettings?.cycle_start_date) {
          const [year, month] = weeklyOffSettings.cycle_start_date
            .split("-")
            .map(Number);
          (settingsPayload as any).base_year = year;
          (settingsPayload as any).base_month = month;
        }

        const shouldSaveSettings = settingsPayload !== null;

        if (!shouldSaveSettings && !shouldSaveNurses) {
          setHasUnsavedChanges(false);
          return;
        }

        const onNursesSaveSuccess = () => {
          updateInitialAssignments();
        };

        const onFinalSuccess = () => {
          if (shouldSaveNurses) {
            onNursesSaveSuccess();
          }
          setHasUnsavedChanges(false);
          if (settingsPayload) {
            updateInitialSettings();
          }
          toast.success("저장되었습니다.");
          // 화면 이탈 방지 모달에서 저장 후 모달닫기
          options?.onSuccess?.();
        };

        // 주휴 설정 저장
        if (shouldSaveSettings) {
          saveSettings(
            {
              payload: settingsPayload,
              groupId: isAdmin ? (selectedGroupId ?? undefined) : undefined,
            },
            {
              onSuccess: () => {
                if (shouldSaveNurses) {
                  saveWeeklyOffNurses(
                    {
                      payload: nurseChanges,
                      groupId: isAdmin
                        ? (selectedGroupId ?? undefined)
                        : undefined,
                    },
                    {
                      onSuccess: onFinalSuccess,
                      onError: (error) =>
                        console.error("주휴 간호사 배치 저장 실패:", error),
                    }
                  );
                } else {
                  onFinalSuccess();
                }
              },
            }
          );
          // 2. 주휴 간호사 배치 변경사항 저장
        } else if (shouldSaveNurses) {
          saveWeeklyOffNurses(
            {
              payload: nurseChanges,
              groupId: isAdmin ? (selectedGroupId ?? undefined) : undefined,
            },
            {
              onSuccess: onFinalSuccess,
              onError: (error) =>
                console.error("주휴 간호사 배치 저장 실패:", error),
            }
          );
        }
      },
      [
        calculateChanges,
        getSettingsPayload,
        updateInitialAssignments,
        updateInitialSettings,
        saveSettings,
        saveWeeklyOffNurses,
        isAdmin,
        selectedGroupId,
        fetchNurseSchema,
      ]
    );

    // ===== 변경사항 감지 =====
    useEffect(() => {
      const nurseChanges = hasNurseChanges();
      setHasUnsavedChanges(nurseChanges || hasSettingsChanges);
    }, [assignedNursesByDay, hasNurseChanges, hasSettingsChanges]);

    const handleReset = useCallback(() => {
      resetSettings();
      resetAssignments();
      setHasUnsavedChanges(false);
    }, [resetSettings, resetAssignments]);

    // ===== 페이지 이탈 방지 (커스텀 훅) =====
    const {
      showNavigationModal,
      handleCancelNavigation, // 저장 안 함 (Discard)
      proceedNavigation, // 저장 후 이동 (Proceed)
    } = useUnsavedChanges(hasUnsavedChanges, handleReset);

    // 저장 후 네비게이션 진행
    const handleSaveAndNavigate = useCallback(() => {
      handleSave({
        onSuccess: () => {
          proceedNavigation();
          handleReset();
          setHasUnsavedChanges(false);
        },
      });
    }, [handleSave, proceedNavigation, handleReset]);

    // ===== ref 노출 =====
    useImperativeHandle(ref, () => ({
      hasUnsavedChanges,
      handleSave,
      handleReset,
    }));

    const isInitialLoading = isNursesListLoading || isWeeklyOffSettingsLoading;
    if (isInitialLoading) {
      return (
        <div className="flex min-h-[200px] w-full items-center justify-center py-12">
          <LoadingSpiner msg="주휴 설정을 불러오는 중..." />
        </div>
      );
    }

    return (
      <div
        ref={scrollContainerRef}
        id="conf-tab3-container"
        className="relative pb-[50px]"
        style={{ width: "fit-content", alignSelf: "center" }}
      >
        {/* 주휴 설정 컴포넌트 */}
        <WeeklyOffSettings
          weeklyOffSettings={weeklyOffSettings}
          handleSettingsChange={handleSettingsChange}
          formatDate={formatDate}
          getDateValue={getDateValue}
          isValidDateString={isValidDateString}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
        />

        {/* 요일 탭*/}
        <WeeklyOffDayTabs
          daysOfWeekTabs={daysOfWeekTabs}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          activeNurseCnt={nurses?.length ?? 0}
        />

        {/* 검색 영역 */}
        <WeeklyOffSearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchTermChange}
          onSearchKeyDown={handleSearchKeyDown}
          onSearch={handleSearch}
        />
        {selectedDay !== "총 배치인원" ? (
          <>
            <WeeklyOffTable
              WeeklyHolidayTableHeaderTitle={`${selectedDay} 주휴배치`}
              list={assignedNursesByDay[selectedDay as ActualDayOfWeek] || []}
              setList={handleSetAssignedNurses}
              nurseCount={
                daysOfWeekTabs.find((d) => d.label === selectedDay)?.count || 0
              }
              highlightedNurseId={highlightedNurseId}
              // 프리셉터 이름
              getPreceptorInfo={getPreceptorInfo}
              selectedGroupId={selectedGroupId}
            />

            <WeeklyOffTable
              WeeklyHolidayTableHeaderTitle={"미지정"}
              disabledColor
              list={unassignedNurses}
              setList={setUnassignedNurses}
              nurseCount={unassignedNurses.length}
              highlightedNurseId={highlightedNurseId}
              // 프리셉터 이름
              getPreceptorInfo={getPreceptorInfo}
              className="mt-[30px]"
              selectedGroupId={selectedGroupId}
            />
          </>
        ) : (
          <WeeklyOffTable
            WeeklyHolidayTableHeaderTitle={`총 배치인원 주휴배치`}
            list={nurses || []} // 전체 간호사 표시
            setList={() => {}}
            disabledColor
            nurseCount={nurses?.length || 0} // 전체 간호사 수
            getAssignedDayForNurse={getAssignedDayForNurse}
            showDayColumn
            highlightedNurseId={highlightedNurseId}
            // 프리셉터 이름
            getPreceptorInfo={getPreceptorInfo}
            selectedGroupId={selectedGroupId}
          />
        )}

        {/* 하단 고정 버튼 그룹 */}
        {visible && (
          <WeeklyOffBottomButtons
            onOpenFullNurse={() => {
              if (
                weeklyOffSettingsData?.base_year &&
                weeklyOffSettingsData?.base_month
              ) {
                setOpenModalWeekOffFullNurse(true);
              }
            }}
            onSave={() => setSaveConfirmOpen(true)}
            // 모달이 열렸을 때(주휴설정이 불가할 때) 하단 버튼을 가리는 가림막 용도
            isDisabled={!serverConfig?.weekly_off_group}
            isBtnDisabled={
              !weeklyOffSettingsData?.base_year ||
              !weeklyOffSettingsData?.base_month
            }
          />
        )}
        {/* 전체 간호사 주휴배치 보기 모달 */}
        {openModalWeekOffFullNurse && (
          <ModalWeekOffFullNurse
            weeklyOffSetYear={weeklyOffSettingsData?.base_year}
            weeklyOffSetMonth={weeklyOffSettingsData?.base_month}
            nursesList={nursesWithCurrentWeeklyOff as NurseResponse[]}
            onClose={() => {
              setOpenModalWeekOffFullNurse(false);
            }}
            selectedGroupId={selectedGroupId}
          />
        )}

        {/* 변경사항 저장 확인 모달 (페이지 이탈 시 감지용) */}
        {showNavigationModal && (
          <CustomModal.Confirm
            isOpen={true}
            onClose={handleCancelNavigation}
            onConfirm={handleSaveAndNavigate}
            isPending={isSaving}
            pendingMsg="저장 중..."
          />
        )}

        {/* 저장하기 클릭 시 저장할까요? 확인 모달 */}
        <CustomModal.Confirm
          isOpen={saveConfirmOpen}
          onClose={() => setSaveConfirmOpen(false)}
          onConfirm={() =>
            handleSave({ onSuccess: () => setSaveConfirmOpen(false) })
          }
          isPending={isSaving}
          pendingMsg="주휴 설정 저장 중..."
          title="주휴 설정을 저장하시겠습니까?"
          confirmText="예"
          cancelText="아니오"
        />

        {/* 주휴 설정이 off경우 나타나는 모달 */}
        <WeekOffAlertModal
          isOpen={!serverConfig?.weekly_off_group}
          portalTargetId="conf-tab3-container" // 명시적으로 전달
          currentConfig={serverConfig}
          isAdmin={isAdmin}
          selectedGroupId={selectedGroupId}
        />
      </div>
    );
  }
);

export default ConfTab3;
